/**
 * Flow scene — spark particle system.
 *
 * A fixed-capacity particle pool: when a note crosses the strike bar a burst of
 * sparks shoots out at the strike point. Pure data + mutation helpers (no
 * React); FlowScene owns the buffer and renders it as ONE InstancedMesh.
 *
 * The pool is deliberately larger than the per-hit maximum so overlapping
 * bursts (two lanes hit on the same beat, or a hold spraying every 70ms) don't
 * starve each other. Idle sparks are free: FlowScene compacts the live ones to
 * the front of the instance buffer and only draws that many.
 */

import * as THREE from 'three';
import { FLOW_LANE_COLORS, type FlowLaneIndex } from './types';
import { FLOOR_Y, STRIKE_Z } from './flowSceneConstants';
import type { FlowSparkShape } from './flowSettings';

export interface Spark {
  active: boolean;
  bornAt: number;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  color: THREE.Color;
  life: number; // seconds remaining
  /** Per-particle phase so turbulence pushes every spark a different way. */
  seed: number;
}

/** Highest per-hit count the UI offers. */
export const MAX_SPARKS_PER_HIT = 1000;

/** Pool size — headroom above the per-hit cap so bursts can overlap.
 *  Costs nothing when idle: instances are drawn up to the live count only. */
export const MAX_SPARKS = 2400;

export function emptySpark(): Spark {
  return {
    active: false,
    bornAt: 0,
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    color: new THREE.Color('#FFFFFF'),
    life: 0,
    seed: 0,
  };
}

export interface SparkSpawnOptions {
  /** How many sparks to try to activate. */
  count: number;
  /** Seconds each spark lives. */
  lifetime: number;
  /** Launch speed range — each spark picks a random speed between the two. */
  speedMin: number;
  speedMax: number;
  /** Burst silhouette: a cone shooting up, or a cube expanding outwards. */
  shape: FlowSparkShape;
  /** Size (world units) of the cube the sparks are BORN inside. Gives the
   *  burst a boxy silhouette from frame one instead of growing out of a point. */
  spread: number;
}

/** How much of the speed is added upward in box mode. Without it a uniform
 *  cube sends half the sparks straight through the floor and the burst reads
 *  as a puff rather than a hit. */
const BOX_UP_BIAS = 0.4;

/** Activate up to `opts.count` idle sparks at the strike point for `lane`. */
export function spawnSparks(
  buffer: Spark[],
  x: number,
  lane: FlowLaneIndex,
  now: number,
  laneColors: readonly string[],
  opts: SparkSpawnOptions,
): void {
  const { count, lifetime, shape, spread } = opts;
  const sMin = Math.min(opts.speedMin, opts.speedMax);
  const sMax = Math.max(opts.speedMin, opts.speedMax);
  const box = shape === 'box';
  let placed = 0;

  for (let i = 0; i < buffer.length && placed < count; i++) {
    const s = buffer[i];
    if (s.active) continue;
    s.active = true;
    s.bornAt = now;
    s.life = lifetime;
    s.seed = Math.random() * Math.PI * 2;

    // Each spark gets its own speed inside the range — that spread is what
    // makes a burst read as depth instead of a flat shell.
    const speed = sMin + Math.random() * (sMax - sMin);

    if (box) {
      s.position.set(
        x + (Math.random() - 0.5) * spread,
        FLOOR_Y + 0.1 + Math.random() * spread,
        STRIKE_Z + (Math.random() - 0.5) * spread,
      );
      // Uniform per-axis velocity = a cube-shaped cloud, nudged upward so it
      // still launches off the strike bar.
      s.velocity.set(
        (Math.random() * 2 - 1) * speed,
        (Math.random() * 2 - 1) * speed + speed * BOX_UP_BIAS,
        (Math.random() * 2 - 1) * speed,
      );
    } else {
      s.position.set(
        x + (Math.random() - 0.5) * 0.2,
        FLOOR_Y + 0.1,
        STRIKE_Z + (Math.random() - 0.5) * 0.2,
      );
      // Narrow sideways spread + all the speed upward = the classic cone.
      s.velocity.set(
        (Math.random() - 0.5) * 1.8,
        speed,
        (Math.random() - 0.5) * 1.2,
      );
    }

    s.color.set(laneColors[lane] ?? FLOW_LANE_COLORS[lane]);
    placed++;
  }
}

/**
 * Turbulence — a cheap curl-ish wobble applied as acceleration.
 *
 * Three sine waves at different frequencies, offset by the spark's own seed, so
 * neighbouring particles drift apart instead of moving as a block. Costs three
 * sin/cos per spark per frame and allocates nothing.
 */
export function applyTurbulence(s: Spark, strength: number, t: number, dt: number): void {
  if (strength <= 0) return;
  const p = s.seed;
  s.velocity.x += Math.sin(t * 3.1 + p) * strength * dt;
  s.velocity.y += Math.sin(t * 4.3 + p * 2.1) * strength * 0.6 * dt;
  s.velocity.z += Math.cos(t * 2.7 + p * 3.7) * strength * dt;
}
